import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../api/supabase';

const statusColor = { PENDING: 'badge-yellow', APPLIED: 'badge-blue', APPROVED: 'badge-green', REJECTED: 'badge-red', CANCELLED: 'badge-gray' };
const PAGE_SIZE = 20;

export default function VisaList() {
  const [visas, setVisas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);

    let clientIds = [];
    if (q) {
      const { data: matchedClients } = await supabase
        .from('clients').select('id').ilike('full_name', `%${q}%`).limit(50);
      clientIds = (matchedClients || []).map(c => c.id);
    }

    let query = supabase.from('visas')
      .select('*, clients(full_name), visa_types(name, country), cost_currency:cost_currency_id(code), invoice:invoice_id(id, invoice_number)', { count: 'exact' })
      .order('created_at', { ascending: false })
      .range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1);

    if (q) {
      const orParts = `visa_ref.ilike.%${q}%,applicant_name.ilike.%${q}%,country.ilike.%${q}%,passport_number.ilike.%${q}%`;
      if (clientIds.length > 0) {
        query = query.or(`${orParts},client_id.in.(${clientIds.join(',')})`);
      } else {
        query = query.or(orParts);
      }
    }

    if (statusFilter) query = query.eq('visa_status', statusFilter);
    const { data, count } = await query;
    setVisas(data || []);
    setTotal(count || 0);
    setLoading(false);
  }, [q, statusFilter, page]);

  useEffect(() => { setPage(0); }, [q, statusFilter]);
  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">Visas</div><div className="page-subtitle">History — new visas created at invoicing stage</div></div>
      </div>
      <div className="card">
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <input className="form-input" placeholder="Search visa ref..." value={q} onChange={e => setQ(e.target.value)} style={{ maxWidth: 260 }} />
          <div style={{ display: 'flex', gap: 6 }}>
            {[['', 'All'], ['PENDING', 'Pending'], ['APPLIED', 'Applied'], ['APPROVED', 'Approved'], ['REJECTED', 'Rejected']].map(([v, l]) => (
              <button key={v} className={`btn btn-sm ${statusFilter === v ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setStatusFilter(v)}>{l}</button>
            ))}
          </div>
        </div>
        {loading ? <div className="spinner" /> : (
          <>
            <div className="table-wrapper">
              <table>
                <thead><tr><th>Ref</th><th>Client</th><th>Visa Type</th><th>Country</th><th>Applied</th><th>Approved</th><th>Expiry</th><th style={{ textAlign: 'right' }}>Cost</th><th>Status</th><th>Payment</th><th>Invoice</th><th></th></tr></thead>
                <tbody>
                  {visas.map(v => (
                    <tr key={v.id}>
                      <td style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--primary)' }}>{v.visa_ref}</td>
                      <td>{v.clients?.full_name || ''}</td>
                      <td>{v.visa_types?.name || ''}</td>
                      <td>{v.visa_types?.country || ''}</td>
                      <td>{v.application_date?.slice(0, 10) || ''}</td>
                      <td>{v.approval_date?.slice(0, 10) || ''}</td>
                      <td>{v.expiry_date?.slice(0, 10) || ''}</td>
                      <td style={{ textAlign: 'right' }} className="amount">{v.cost_currency?.code} {parseFloat(v.cost_price || 0).toLocaleString()}</td>
                      <td><span className={`badge ${statusColor[v.visa_status] || 'badge-gray'}`}>{v.visa_status}</span></td>
                      <td><span className={`badge ${v.payment_status === 'PAID' ? 'badge-green' : 'badge-yellow'}`}>{v.payment_status}</span></td>
                      <td>{v.invoice ? <Link to={`/invoices/${v.invoice.id}`} style={{ color: 'var(--primary)', fontSize: 12, fontWeight: 600 }}>{v.invoice.invoice_number}</Link> : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>}</td>
                      <td><Link to={`/visas/${v.id}`} className="btn btn-secondary btn-sm">View</Link></td>
                    </tr>
                  ))}
                  {visas.length === 0 && <tr><td colSpan={11}><div className="empty-state"><div className="empty-state-title">No visas found</div></div></td></tr>}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, fontSize: 13, color: 'var(--text-muted)' }}>
                <span>{total} records &nbsp;·&nbsp; Page {page + 1} of {totalPages}</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => setPage(0)} disabled={page === 0}>«</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setPage(p => p - 1)} disabled={page === 0}>‹ Prev</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}>Next ›</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}>»</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
